import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";

export default function ChallengePage() {
  const lastUpdated = "07 July 2026";

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "The Handicalc Challenge | Rules, Terms & Privacy",
    description:
      "The Handicalc Challenge rules, eligibility, prize details and privacy notice for entrants.",
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://readdy.ai/api/search-image?query=Abstract%20artistic%20golf%20course%20landscape%20with%20rolling%20fairways%20and%20flagstick%20silhouette%20against%20warm%20golden%20sky%2C%20soft%20painterly%20brush%20strokes%2C%20deep%20emerald%20green%20and%20rich%20amber%20gold%20colour%20palette%2C%20atmospheric%20editorial%20composition%20suitable%20as%20dark%20background%20with%20text%20overlay&width=1600&height=600&seq=challenge-hero-bg&orientation=landscape')",
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/75"></div>
          <div className="relative mx-auto max-w-content px-4 md:px-6 pt-20 md:pt-24 pb-12 md:pb-16 text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500 backdrop-blur border border-accent-400 text-xs font-bold text-foreground-950 uppercase tracking-wider">
              <i className="ri-trophy-line"></i>
              Free Competition
            </span>
            <h1 className="mt-6 font-heading font-bold text-background-50 text-3xl md:text-5xl lg:text-6xl leading-tight max-w-3xl mx-auto">
              The Handicalc Challenge
            </h1>
            <p className="mt-5 text-background-100/90 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Complete the Handicalc Challenge in the app for your chance to win a place in the{" "}
              <strong className="text-background-50">Handicalc Team</strong> at the Dan Grieve Invitational on{" "}
              <strong className="text-background-50">27 August 2026</strong>.
            </p>
            <p className="mt-3 text-background-100/60 text-sm">Last updated: {lastUpdated}</p>
          </div>
        </section>

        {/* Content */}
        <section className="bg-background-50 py-12 md:py-20">
          <div className="mx-auto max-w-3xl px-4 md:px-6 space-y-6 md:space-y-8">
            {/* Challenge Summary */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-4">
                Challenge Summary
              </h2>
              <ul className="space-y-2.5 text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                <li className="flex gap-2">
                  <span className="text-primary-600 font-semibold shrink-0">Prize:</span>
                  <span>One place in the Handicalc Team at the Dan Grieve Invitational on 27 August 2026</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary-600 font-semibold shrink-0">Entry:</span>
                  <span>Through the Handicalc app only</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary-600 font-semibold shrink-0">Entry Fee:</span>
                  <span>Free</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary-600 font-semibold shrink-0">Eligibility:</span>
                  <span>Age 18+ • UK or Republic of Ireland resident • Handicap Index not exceeding 24.0 (men) or 28.0 (women)</span>
                </li>
              </ul>
            </article>

            {/* Get Handicalc */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                Get Handicalc
              </h2>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-4">
                Don&apos;t have Handicalc yet? Download the free app and complete the Challenge.
              </p>
              <div className="flex items-center gap-4 flex-wrap">
                <a
                  href="https://apps.apple.com/gb/app/handicalc/id6757720525"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-[140px] h-[48px] flex-shrink-0 cursor-pointer transition-transform hover:scale-105"
                  aria-label="Download HANDICALC on the App Store"
                >
                  <img
                    src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg"
                    alt="Download on the App Store"
                    className="w-full h-full object-contain"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.wnapp.id1773163661064"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-[140px] h-[48px] flex-shrink-0 cursor-pointer transition-transform hover:scale-105"
                  aria-label="Get HANDICALC on Google Play"
                >
                  <img
                    src="https://public.readdy.ai/ai/img_res/edited_714094e88e03320dda08007cdcd15bfb_2a3910ca.jpg"
                    alt="Get it on Google Play"
                    className="w-full h-full object-contain"
                  />
                </a>
              </div>
              <p className="mt-4 text-foreground-500 text-sm">
                Handicalc is free to download and free to use until at least{" "}
                <strong className="text-foreground-700">31 December 2026</strong>.
              </p>
            </article>

            {/* About the Challenge */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                About the Challenge
              </h2>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                The Handicalc Challenge is a free promotional competition organised by{" "}
                <strong className="text-foreground-950">Handicalc Ltd</strong>. It is designed to help golfers discover key features of the Handicalc app.
              </p>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-4">
                The Challenge can only be accessed from within the Handicalc app. It is not available as a public online entry form.
              </p>
              <div className="rounded-lg bg-accent-100/70 border border-accent-200/50 p-4 text-sm text-foreground-700 leading-relaxed">
                There is no purchase necessary to enter. Handicalc is free to use until at least 31 December 2026.
              </div>
            </article>

            {/* Eligibility + Prize */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                  Eligibility
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                  To enter the Handicalc Challenge, you must:
                </p>
                <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                  <li>Be aged <strong className="text-foreground-950">18 or over</strong>;</li>
                  <li>Be a <strong className="text-foreground-950">resident of the United Kingdom or the Republic of Ireland</strong>;</li>
                  <li>Hold an <strong className="text-foreground-950">active World Handicap System™ (WHS) Handicap Index</strong> with a valid <strong className="text-foreground-950">Handicap ID issued by your national golf association or governing body</strong>;</li>
                  <li>Successfully complete <strong className="text-foreground-950">all five Handicalc Challenges</strong>;</li>
                  <li>Register your entry within the Handicalc app.</li>
                </ul>
                <p className="mt-4 text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  Failure to satisfy all eligibility requirements may result in disqualification.
                </p>
              </article>

              <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                  Prize
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                  The prize is <strong className="text-foreground-950">one place in the Handicalc Team at the Dan Grieve Invitational on 27 August 2026</strong>.
                </p>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-2">The prize:</p>
                <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                  <li>is non-transferable;</li>
                  <li>has no cash alternative;</li>
                  <li>cannot be exchanged for another prize;</li>
                  <li>does not include travel, accommodation or personal expenses unless expressly stated.</li>
                </ul>
                <p className="mt-4 text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  If the winner cannot attend, Handicalc Ltd may offer the prize to another eligible entrant.
                </p>
              </article>
            </div>

            {/* Winner Selection */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                Winner Selection
              </h2>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                The prize winner will be selected by a random draw from all eligible entries received through the Handicalc app.
              </p>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                Only entrants who successfully complete all five Handicalc Challenges, submit a valid entry and satisfy all eligibility requirements will be entered into the draw.
              </p>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                Each eligible entrant may only be entered into the draw once. The chances of winning will depend on the total number of eligible entries received.
              </p>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                Handicalc Ltd&apos;s decision regarding eligibility, the draw process and prize allocation is final.
              </p>
            </article>

            {/* Personal Information */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                Personal Information We Collect
              </h2>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                To administer the competition, we collect the following information from each entrant:
              </p>
              <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed mb-4">
                <li>full name;</li>
                <li>email address;</li>
                <li>Handicap Index;</li>
                <li>Handicap ID;</li>
              </ul>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-2">This information is required to:</p>
              <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed mb-4">
                <li>verify eligibility;</li>
                <li>administer the competition;</li>
                <li>contact the winner;</li>
                <li>award the prize.</li>
              </ul>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                Providing this information is voluntary, but it is required if you wish to enter the Challenge.
              </p>
            </article>

            {/* How We Use Your Information */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                How We Use Your Information
              </h2>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                Your personal information will only be used to administer the Handicalc Challenge.
              </p>
              <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed mb-4">
                <li>We do not sell your personal information.</li>
                <li>We do not use your information for marketing unless you separately choose to receive marketing communications.</li>
                <li>We only share information where reasonably necessary to administer the competition or where required by law.</li>
              </ul>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                We process this information because it is necessary to administer your competition entry, verify eligibility and award the prize.
              </p>
            </article>

            {/* Data Retention + Your Rights */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                  Data Retention
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                  Competition entry information will only be retained for as long as reasonably necessary to administer the competition, resolve any queries or disputes, and comply with legal obligations.
                </p>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  After this period, your information will be securely deleted or anonymised.
                </p>
              </article>

              <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                  Your Rights
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-2">You may request:</p>
                <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed mb-4">
                  <li>access to your personal information;</li>
                  <li>correction of inaccurate information;</li>
                  <li>deletion of your information where applicable;</li>
                  <li>restriction of certain processing;</li>
                  <li>to object to certain processing.</li>
                </ul>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  You also have the right to complain to the Information Commissioner&apos;s Office if you are unhappy with how your personal information is handled.
                </p>
              </article>
            </div>

            {/* Marketing */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                Marketing
              </h2>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                Entering the Handicalc Challenge does not automatically subscribe you to marketing emails.
              </p>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                If you would like to receive Handicalc news, product updates or future competition information, you may opt in separately. Marketing consent is optional and is not required to enter the Challenge.
              </p>
            </article>

            {/* App Privacy */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                App Privacy
              </h2>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                These Rules, Terms and Privacy Notice apply only to information submitted as part of the Handicalc Challenge.
              </p>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                Normal use of the Handicalc app remains covered by the Handicalc App Privacy Policy. The app does not require an account and stores user data locally on your device as described in that policy.
              </p>
            </article>

            {/* Competition Rules */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                Competition Rules
              </h2>
              <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                <li>Handicalc Ltd reserves the right to verify eligibility.</li>
                <li>Entries containing false, incomplete or misleading information may be disqualified.</li>
                <li>The prize is non-transferable and no cash alternative is available.</li>
                <li>Handicalc Ltd accepts no responsibility for technical failures, lost submissions or circumstances outside its reasonable control.</li>
                <li>Handicalc Ltd may amend, suspend or cancel the competition where reasonably necessary.</li>
                <li>Any significant changes will be published on this page.</li>
                <li>Nothing in these terms excludes liability that cannot legally be excluded.</li>
              </ul>
            </article>

            {/* Governing Law */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                Governing Law
              </h2>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                These Rules, Terms and Privacy Notice are governed by the laws of England and Wales.
              </p>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </article>

            {/* Entry Declaration */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                Entry Declaration
              </h2>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                Before submitting an entry through the Handicalc app, entrants must confirm that:
              </p>
              <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed mb-4">
                <li>they are eligible to enter;</li>
                <li>the information they provide is accurate;</li>
                <li>they have read and agree to these Rules, Terms and Privacy Notice.</li>
              </ul>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                Any optional marketing consent must be requested separately and must not be required for entry.
              </p>
            </article>

            {/* Promoter */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                Promoter
              </h2>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                The Handicalc Challenge is promoted by <strong className="text-foreground-950">Handicalc Ltd</strong>.
              </p>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                Registered in England and Wales.
                <br />
                Company number: 16982268
                <br />
                Registered office: Office 7044 58 Peregrine Road, Hainault, Ilford, Essex, England
              </p>
            </article>

            {/* Contact */}
            <article className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl mb-3">
                Contact
              </h2>
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-3">
                If you have any questions about the Handicalc Challenge or how your personal information is used, please contact:
              </p>
              <div className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                <p className="font-semibold text-foreground-950">Handicalc Ltd</p>
                <p>
                  Email:{" "}
                  <a
                    href="mailto:support@handicalc.com"
                    className="text-primary-700 hover:text-primary-800 font-medium underline underline-offset-2"
                  >
                    support@handicalc.com
                  </a>
                </p>
                <p>
                  Website:{" "}
                  <a
                    href="https://www.handicalc.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-700 hover:text-primary-800 font-medium underline underline-offset-2"
                  >
                    www.handicalc.com
                  </a>
                </p>
              </div>
            </article>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-background-50 pb-16 md:pb-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="relative overflow-hidden rounded-3xl">
              <img
                src="https://readdy.ai/api/search-image?query=Golf%20course%20fairway%20stretching%20into%20golden%20sunset%20with%20dramatic%20sky%20clouds%20and%20distant%20green%2C%20warm%20atmospheric%20light%2C%20painterly%20editorial%20style%2C%20rich%20emerald%20green%20and%20amber%20gold%20colour%20palette%2C%20cinematic%20landscape%20composition%20suitable%20as%20dark%20background%20with%20text%20overlay&width=1600&height=600&seq=challenge-cta-bg&orientation=landscape"
                alt="Golf course at sunset"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-900/75 to-primary-800/60"></div>
              <div className="relative z-10 px-6 md:px-14 py-14 md:py-20 text-center max-w-3xl mx-auto">
                <h2 className="font-heading font-bold text-background-50 text-3xl md:text-4xl leading-tight">
                  Ready to take the Challenge?
                </h2>
                <p className="mt-4 text-background-100/90 text-base md:text-lg leading-relaxed">
                  Download the Handicalc app, complete all five Challenges, and you could be joining the Handicalc Team at the Dan Grieve Invitational.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                  <Link
                    to="/calculator"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm md:text-base font-semibold whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Try the calculator <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-background-50/10 hover:bg-background-50/20 backdrop-blur border border-background-50/30 text-background-50 text-sm md:text-base font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Contact us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}